import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Alert, Button, Spin, Switch, Table, Tooltip } from 'antd';

import { useQueryClient } from '@tanstack/react-query';
import ProductClass from 'classes/Product';
import { Formik, Form as FormikForm, Field, FieldArray } from 'formik';
import type { FormikHelpers } from 'formik';
import type IProduct from 'interfaces/IProduct';
import { CharacterCounterFormField } from 'v2/components/Shared/FormFields/CharacterCounterFormField';
import CurrencyFormField from 'v2/components/Shared/FormFields/CurrencyFormField';
import TextFormField from 'v2/components/Shared/FormFields/TextFormField';
import { OfferCurrencyAlert } from 'v2/components/Shared/OfferCurrencyAlert';
import { Space } from 'v2/components/Shared/Space';
import { getCurrencySymbol } from 'v2/helpers/getCurrencySymbol';
import { isHighTicketOffer, isHighTicketValue, loadActiveCoproducers } from 'v2/helpers/highTicket';
import { useCreateProductOffer } from 'v2/hooks/useCreateProductOffer';
import type { Currency } from 'v2/interfaces/currency';
import { highTicketCampaignClient } from 'v2/services/highTicketCampaign';
import productService from 'v2/services/product';
import * as yup from 'yup';

import { Icon } from '@eduzz/ui-antd-theme';

import { HighTicketTerms } from '@myeduzz-vertical/root/HighTicketCampaignTerms';

import { ChooseCurrenciesModal } from '../ChooseCurrenciesModal';

const MIN_CURRENCY_VALUE = 5;

const validationSchema = yup.object().shape({
  name: yup.string().required('Nome da oferta é obrigatório').min(6).max(100),
  description: yup.string().optional().min(100).max(4000),
  prices: yup
    .array()
    .of(
      yup.object().shape({
        value: yup.number().required().min(MIN_CURRENCY_VALUE, 'O valor deve ser no mínimo 5'),
        currency_code: yup.string().required()
      })
    )
    .required()
});

type FormValues = {
  name: string;
  description: string;
  is_visible_affiliate: boolean;
  prices: Array<{
    value: number;
    currency_code: string;
  }>;
};

type FormProps = {
  product: IProduct;
};

function Form({ product }: FormProps) {
  const productId = product.id;
  const history = useHistory();
  const queryClient = useQueryClient();
  const [isChooseCurrenciesModalOpen, setIsChooseCurrenciesModalOpen] = React.useState(false);
  const [highTicketTermOpen, setHighTicketTermOpen] = React.useState(false);
  const [hasAcceptedHighTicketTerms, setHasAcceptedHighTicketTerms] = React.useState(
    !!product.accepted_highticket_terms
  );
  const [createdOfferId, setCreatedOfferId] = React.useState<number>();
  const [createdOfferValue, setCreatedOfferValue] = React.useState<number>();
  const [selectedCurrencies, setSelectedCurrencies] = React.useState<Currency[]>([]);
  const createOfferMutation = useCreateProductOffer({
    onError: () => {
      toast.error('Erro ao criar oferta');
    }
  });

  function addCurrencies(currencies: Currency[]) {
    setSelectedCurrencies([...selectedCurrencies, ...currencies]);
  }

  function removeCurrency(currencyId: number) {
    setSelectedCurrencies(selectedCurrencies.filter(selectedCurrency => selectedCurrency.id !== currencyId));
  }

  function handleCancel() {
    history.push('/producer/product/editproduct/' + productId + '/sales');
  }

  const leaveAfterCreate = React.useCallback(() => {
    toast.success('Oferta criada com sucesso');
    history.push('/producer/product/editproduct/' + productId + '/sales');
  }, [history, productId]);

  const resolveCreatedOfferId = React.useCallback(
    async (createdId: number | undefined, name: string) => {
      if (createdId) {
        return createdId;
      }

      const offers = await productService.getOffers(productId);
      return offers.filter(offer => offer.name === name).sort((left, right) => right.id - left.id)[0]?.id;
    },
    [productId]
  );

  const onSubmit = React.useCallback(
    async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
      if (values.description === '') {
        delete values.description;
      }

      try {
        const created = await createOfferMutation.mutateAsync({
          productId,
          ...values
        });

        if (!isHighTicketOffer(values.prices)) {
          leaveAfterCreate();
          return;
        }

        let offerId: number | undefined;

        try {
          offerId = await resolveCreatedOfferId(created?.id, values.name);
        } catch {
          toast.error('Erro ao criar oferta');
          return;
        }

        if (!offerId) {
          toast.error('Erro ao criar oferta');
          return;
        }

        setCreatedOfferId(offerId);
        setCreatedOfferValue(values.prices.find(price => price.currency_code === 'BRL')?.value);
        setHighTicketTermOpen(true);
      } finally {
        setSubmitting(false);
      }
    },
    [createOfferMutation, leaveAfterCreate, productId, resolveCreatedOfferId]
  );

  const acceptTerms = React.useCallback(async () => {
    if (hasAcceptedHighTicketTerms || !isHighTicketValue(createdOfferValue)) {
      return true;
    }

    try {
      await productService.updateProduct(product.id, {
        ...new ProductClass(product).serviceFormat(),
        coproducers: await loadActiveCoproducers(product.id),
        accepted_highticket_terms: true
      });
      queryClient.removeQueries({ queryKey: ['product', product.id] });
      setHasAcceptedHighTicketTerms(true);
      return true;
    } catch (error) {
      toast.error(error);
      return false;
    }
  }, [createdOfferValue, hasAcceptedHighTicketTerms, product, queryClient]);

  const brlColumns = [
    { title: 'Moeda', width: 200, render: () => 'BRL (Real Brasileiro)' },
    {
      title: 'Valor',
      width: 200,
      render: () => (
        <CurrencyFormField
          id='offer-price-BRL'
          name='prices.0.value'
          InputProps={{
            startAdornment: <span className='mr-1'>R$</span>
          }}
          inputProps={{
            maxLength: 20
          }}
        />
      )
    },
    { title: ' ', render: (): React.ReactNode => null }
  ];

  return (
    <Formik
      initialValues={{
        name: '',
        description: '',
        is_visible_affiliate: false,
        prices: [{ value: 0, currency_code: 'BRL' }]
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, values, setFieldValue }) => (
        <FormikForm className='flex flex-col gap-6'>
          <Field id='offer-name' name='name' component={TextFormField} label='Nome da oferta' maxLength={255} />
          <Field
            id='offer-description'
            name='description'
            component={CharacterCounterFormField}
            label='Descrição da oferta para checkout (opcional)'
            limits={4000}
            minimum={100}
          />
          <div className='flex items-center gap-2'>
            <Switch
              checked={values.is_visible_affiliate}
              onChange={checked => setFieldValue('is_visible_affiliate', checked)}
            />
            <span>Visível para afiliados</span>
          </div>
          <FieldArray name='prices'>
            {({ push, remove }) => (
              <>
                <div className='bg-surface-default rounded-sm p-4'>
                  <Table
                    columns={brlColumns}
                    dataSource={[{ key: 'brl' }]}
                    pagination={false}
                    size='small'
                    showHeader
                  />
                  <Space size={6} axis='vertical' />
                  <Alert
                    type='info'
                    message='É obrigatória a configuração do valor em real para a conversão automática de suas vendas.'
                  />
                  <Space size={32} axis='vertical' />
                  <div className='flex items-center justify-between'>
                    <span>Cadastre valores diferenciados para moedas internacionais</span>
                    <Button id='add-currency-button' onClick={() => setIsChooseCurrenciesModalOpen(true)}>
                      Cadastrar novo valor
                    </Button>
                  </div>
                  <Space size={16} axis='vertical' />
                  <Table
                    size='small'
                    pagination={false}
                    locale={{ emptyText: 'Nenhuma moeda cadastrada' }}
                    dataSource={selectedCurrencies.map((row, index) => ({ ...row, _index: index }))}
                    rowKey='id'
                    columns={[
                      {
                        title: 'Moeda',
                        width: 200,
                        render: (_: any, row: any) => (
                          <span className='block max-w-37.5 truncate'>
                            {row.code} ({row.name})
                          </span>
                        )
                      },
                      {
                        title: 'Valor',
                        render: (_: any, row: any) => (
                          <CurrencyFormField
                            id={`offer-price-${row.code}`}
                            name={`prices.${row._index + 1}.value`}
                            currency={row.code}
                            inputProps={{
                              maxLength: 20
                            }}
                            InputProps={{
                              startAdornment: <span className='mr-1'>{getCurrencySymbol('pt-BR', row.code)}</span>
                            }}
                          />
                        )
                      },
                      {
                        title: 'Ações',
                        align: 'right' as const,
                        render: (_: any, row: any) => (
                          <Tooltip title='Remover' placement='top'>
                            <Button
                              type='text'
                              shape='circle'
                              id={`remove-currency-button-${row.code}`}
                              onClick={() => {
                                remove(row._index + 1);
                                removeCurrency(row.id);
                              }}
                              icon={<Icon.Trash2 />}
                            />
                          </Tooltip>
                        )
                      }
                    ]}
                  />
                  <Space size={16} axis='vertical' />
                  <OfferCurrencyAlert />
                </div>
                <footer className='flex justify-end gap-4'>
                  <Button id='cancel-offer-button' onClick={handleCancel}>
                    Cancelar
                  </Button>
                  <Button
                    id='create-offer-button'
                    type='primary'
                    htmlType='submit'
                    disabled={isSubmitting}
                    icon={isSubmitting ? <Spin size='small' /> : undefined}
                  >
                    Cadastrar oferta
                  </Button>
                </footer>
                <ChooseCurrenciesModal
                  isOpen={isChooseCurrenciesModalOpen}
                  closeModal={() => setIsChooseCurrenciesModalOpen(false)}
                  selectedCurrencies={selectedCurrencies}
                  addCurrencies={currencies => {
                    currencies.forEach(currency =>
                      push({
                        value: 0,
                        currency_code: currency.code
                      })
                    );
                    addCurrencies(currencies);
                  }}
                />
              </>
            )}
          </FieldArray>
          <HighTicketTerms
            open={highTicketTermOpen}
            productId={Number(product.id)}
            offerId={createdOfferId}
            offerValue={createdOfferValue}
            highTicketTerms={hasAcceptedHighTicketTerms}
            confirmLoading={createOfferMutation.isPending}
            client={highTicketCampaignClient}
            onClose={() => {
              setHighTicketTermOpen(false);
              leaveAfterCreate();
            }}
            onAccepted={acceptTerms}
          />
        </FormikForm>
      )}
    </Formik>
  );
}

export { Form };
